import { useEffect, useMemo, useState } from "react";
import { Truck, Plus, Trash2, Edit3, Loader2, X, Save, MapPin, Calendar, User, Banknote, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATO_BADGE = {
  pianificato: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  in_corso:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
  completato:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  annullato:   "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const MOTIVI = [
  { v: "rebalance", l: "Riequilibrio flotta" },
  { v: "officina",  l: "Officina / Riparazione" },
  { v: "consegna_cliente", l: "Consegna cliente" },
  { v: "restituzione", l: "Restituzione cliente" },
  { v: "acquisto", l: "Acquisto nuovo veicolo" },
  { v: "vendita", l: "Vendita veicolo" },
];

const empty = () => ({
  from_sede_id: "", to_sede_id: "", from_indirizzo: "", to_indirizzo: "",
  car_ids: [], autista_nome: "", autista_telefono: "", bisarca_targa: "",
  data_pianificata: new Date().toISOString().slice(0, 10),
  ora_partenza: "", ora_arrivo_stimata: "",
  km_stimati: 0, costo_carburante: 0, costo_pedaggi: 0, costo_autista: 0, costo_bisarca_esterna: 0,
  stato: "pianificato", note: "", motivo: "rebalance",
});

export default function Transfers() {
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [cars, setCars] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, s] = await Promise.all([
        api.get("/transfers"), api.get("/transfers/kpi/summary"),
        api.get("/cars"),      api.get("/sedi"),
      ]);
      setItems(a.data || []); setKpi(b.data);
      setCars(c.data || []); setSedi(s.data || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditId(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ ...empty(), ...t }); setEditId(t.id); setShowForm(true); };

  const save = async () => {
    if (!form.data_pianificata) { toast.error("Data obbligatoria"); return; }
    try {
      if (editId) {
        await api.put(`/transfers/${editId}`, form);
        toast.success("Trasferimento aggiornato");
      } else {
        await api.post("/transfers", form);
        toast.success("Trasferimento creato");
      }
      setShowForm(false); setEditId(null); load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare il trasferimento?")) return;
    await api.delete(`/transfers/${id}`);
    toast.success("Eliminato"); load();
  };

  const setStato = async (t, stato) => {
    await api.put(`/transfers/${t.id}`, { ...t, stato });
    toast.success(`Stato → ${stato}`);
    load();
  };

  const totaleCostoForm = useMemo(() =>
    [form.costo_carburante, form.costo_pedaggi, form.costo_autista, form.costo_bisarca_esterna]
      .reduce((a, v) => a + (+v || 0), 0), [form]);

  return (
    <div className="space-y-6" data-testid="transfers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Truck className="text-[#ff0033]" size={26} />
            Bisarca & Trasferimenti
          </h1>
          <p className="text-sm text-white/45 mt-1">Pianifica e traccia movimentazioni flotta tra sedi/officine/clienti — calcolo km/costi automatico</p>
        </div>
        <button onClick={openNew} data-testid="new-transfer-btn"
          className="btn-neon px-5 py-2.5 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuovo trasferimento
        </button>
      </div>

      {/* KPI */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Totale" value={kpi.totale} accent="white" icon={Truck} />
          <KPI label="Pianificati" value={kpi.pianificati} accent="sky" icon={Calendar} />
          <KPI label="Km totali" value={`${kpi.km_totali} km`} accent="amber" icon={MapPin} />
          <KPI label="Costo totale" value={`€ ${kpi.costo_totale.toLocaleString("it-IT")}`} accent="emerald" icon={Banknote} />
        </div>
      )}

      {/* TABLE */}
      <div className="glass rounded-2xl overflow-hidden">
        {loading && <div className="text-center py-12 text-white/40">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center py-16">
            <Truck size={36} className="mx-auto text-white/20 mb-3" />
            <div className="text-white/55 mb-3">Nessun trasferimento pianificato</div>
            <button onClick={openNew} className="btn-neon px-4 py-2 rounded-full text-xs">+ Crea il primo</button>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-xs" data-testid="transfers-table">
              <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-2 py-2 text-left">Veicoli</th>
                  <th className="px-2 py-2 text-left">Da → A</th>
                  <th className="px-2 py-2 text-left">Autista</th>
                  <th className="px-2 py-2 text-left">Motivo</th>
                  <th className="px-2 py-2 text-right">Km</th>
                  <th className="px-2 py-2 text-right">Costo</th>
                  <th className="px-2 py-2 text-center">Stato</th>
                  <th className="px-2 py-2 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const fromS = sedi.find((s) => s.id === t.from_sede_id);
                  const toS   = sedi.find((s) => s.id === t.to_sede_id);
                  return (
                    <tr key={t.id} className="border-t border-white/5">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{t.data_pianificata}</div>
                        {t.ora_partenza && <div className="text-[10px] text-white/40">{t.ora_partenza}</div>}
                      </td>
                      <td className="px-2 py-2">
                        {(t.vehicles || []).slice(0, 2).map((v) => (
                          <div key={v.id} className="text-[10px]"><span className="font-mono text-white/70">{v.targa}</span> · {v.marca} {v.modello}</div>
                        ))}
                        {(t.vehicles || []).length > 2 && <div className="text-[9px] text-white/40">+{t.vehicles.length - 2} altri</div>}
                      </td>
                      <td className="px-2 py-2 text-[10px]">
                        {fromS?.nome || t.from_indirizzo || "—"} → {toS?.nome || t.to_indirizzo || "—"}
                      </td>
                      <td className="px-2 py-2">{t.autista_nome || "—"}</td>
                      <td className="px-2 py-2">
                        <span className="text-[10px] uppercase tracking-wide text-white/55">{MOTIVI.find((m) => m.v === t.motivo)?.l || t.motivo}</span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">{t.km_stimati || 0}</td>
                      <td className="px-2 py-2 text-right font-mono">€{(t.costo_totale_stimato || 0).toLocaleString("it-IT")}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${STATO_BADGE[t.stato] || "border-white/10 text-white/50"}`}>{t.stato}</span>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          {t.stato === "pianificato" && <button onClick={() => setStato(t, "in_corso")} className="px-2 py-1 rounded text-[9px] bg-amber-500/15 border border-amber-500/40 text-amber-300" title="Avvia">▶</button>}
                          {t.stato === "in_corso" && <button onClick={() => setStato(t, "completato")} className="px-2 py-1 rounded text-[9px] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300" title="Completa"><CheckCircle2 size={11} /></button>}
                          <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-white/10"><Edit3 size={13} /></button>
                          <button onClick={() => del(t.id)} className="p-1.5 rounded hover:bg-rose-500/20 text-rose-300"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="transfer-form">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold">{editId ? "Modifica trasferimento" : "Nuovo trasferimento"}</h2>
                <p className="text-xs text-white/45 mt-1">Pianifica movimentazione · km e costi calcolati automaticamente</p>
              </div>
              <button onClick={() => setShowForm(false)} className="text-white/40"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Data pianificata *">
                <input type="date" value={form.data_pianificata} onChange={(e) => setForm({ ...form, data_pianificata: e.target.value })}
                  data-testid="tr-data" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </F>
              <F label="Motivo">
                <select value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {MOTIVI.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </F>
              <F label="Sede di partenza">
                <select value={form.from_sede_id} onChange={(e) => setForm({ ...form, from_sede_id: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {sedi.map((s) => <option key={s.id} value={s.id}>{s.nome} · {s.citta}</option>)}
                </select>
              </F>
              <F label="Sede di destinazione">
                <select value={form.to_sede_id} onChange={(e) => setForm({ ...form, to_sede_id: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {sedi.map((s) => <option key={s.id} value={s.id}>{s.nome} · {s.citta}</option>)}
                </select>
              </F>
              <F label="Indirizzo libero da" className="md:col-span-2">
                <input value={form.from_indirizzo} onChange={(e) => setForm({ ...form, from_indirizzo: e.target.value })} placeholder="Es. Officina XYZ, via..." className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </F>
              <F label="Indirizzo libero a" className="md:col-span-2">
                <input value={form.to_indirizzo} onChange={(e) => setForm({ ...form, to_indirizzo: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </F>
              <F label="Autista">
                <input value={form.autista_nome} onChange={(e) => setForm({ ...form, autista_nome: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </F>
              <F label="Telefono autista">
                <input value={form.autista_telefono} onChange={(e) => setForm({ ...form, autista_telefono: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </F>
              <F label="Bisarca esterna (targa)">
                <input value={form.bisarca_targa} onChange={(e) => setForm({ ...form, bisarca_targa: e.target.value.toUpperCase() })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
              <F label="Km stimati">
                <input type="number" value={form.km_stimati} onChange={(e) => setForm({ ...form, km_stimati: +e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
            </div>

            <F label="Veicoli da trasferire" className="mt-3">
              <div className="max-h-44 overflow-y-auto border border-white/10 rounded-lg p-2 bg-white/[0.02]">
                {cars.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-white/[0.04] px-2 rounded">
                    <input type="checkbox" checked={form.car_ids.includes(c.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...form.car_ids, c.id]
                          : form.car_ids.filter((x) => x !== c.id);
                        setForm({ ...form, car_ids: next });
                      }} />
                    <span className="text-xs font-mono text-white/70 w-20">{c.targa}</span>
                    <span className="text-xs">{c.marca} {c.modello}</span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-white/40 mt-1">{form.car_ids.length} veicolo/i selezionato/i</div>
            </F>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              <F label="Carburante €">
                <input type="number" step="0.1" value={form.costo_carburante} onChange={(e) => setForm({ ...form, costo_carburante: +e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
              <F label="Pedaggi €">
                <input type="number" step="0.1" value={form.costo_pedaggi} onChange={(e) => setForm({ ...form, costo_pedaggi: +e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
              <F label="Autista €">
                <input type="number" step="0.1" value={form.costo_autista} onChange={(e) => setForm({ ...form, costo_autista: +e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
              <F label="Bisarca ext. €">
                <input type="number" step="0.1" value={form.costo_bisarca_esterna} onChange={(e) => setForm({ ...form, costo_bisarca_esterna: +e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </F>
            </div>
            <div className="mt-2 text-right text-xs text-white/55">
              Costo totale stimato: <b className="text-emerald-300 font-mono">€ {totaleCostoForm.toFixed(2)}</b>
            </div>

            <F label="Note" className="mt-3">
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </F>

            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button onClick={save} data-testid="transfer-save" className="btn-neon px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                <Save size={14} /> {editId ? "Aggiorna" : "Crea"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[10px] uppercase tracking-widest text-white/40">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const KPI = ({ label, value, accent, icon: Icon }) => {
  const tones = { white: "text-white", emerald: "text-emerald-300", sky: "text-sky-300", amber: "text-amber-300" };
  return (
    <div className="glass rounded-xl p-3 flex items-center gap-3">
      <Icon size={18} className={tones[accent] || "text-white/60"} />
      <div>
        <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
        <div className={`font-display font-black text-xl ${tones[accent] || "text-white"} mt-0.5`}>{value}</div>
      </div>
    </div>
  );
};
